import { MutableRefObject, useEffect } from "react";
import { useEditor } from "@tiptap/react";
// ponytail: statically imported, so @tiptap/markdown (and its `marked`
// dependency) ships in every consumer's bundle regardless of whether they
// use markdown mode — the gate below only avoids *registering* it (paste
// behavior, editor.getMarkdown()) for HTML-only consumers, not its bundle
// cost. Splitting that out would need a dynamic import plus deferring
// editor creation until it resolves, which risks losing in-progress
// content/focus if a consumer starts typing mid-load; not worth it unless
// bundle size becomes a real complaint.
import { Markdown } from "@tiptap/markdown";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle, FontSize } from "@tiptap/extension-text-style";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import Placeholder from "@tiptap/extension-placeholder";
import {
  createMentionExtension,
  MentionCallbacks,
} from "./extensions/MentionExtension";
import { createImageUploadExtension } from "./extensions/ImageUploadExtension";
import { createSafeImageExtension } from "./extensions/SafeImageExtension";
import {
  createYoutubeExtension,
  OnYoutubePaste,
} from "./extensions/YoutubeExtension";
import {
  createLinkKeyboardShortcutExtension,
  OpenLinkPopover,
} from "./extensions/LinkKeyboardShortcutExtension";
import type { RichTextEditorProps } from "../types";

/**
 * Parameters for the useRichTextEditor hook.
 * @property {MutableRefObject<RichTextEditorProps["onImageUpload"]>} onImageUploadRef - Stable ref to the image upload callback, used by extensions without re-creating the editor.
 * @property {MutableRefObject<MentionCallbacks>} mentionCallbacksRef - Stable ref to mention event callbacks (input change, item select).
 * @property {MutableRefObject<OnYoutubePaste>} onYoutubePasteRef - Stable ref to the YouTube paste handler used by the YouTube extension.
 * @property {MutableRefObject<OpenLinkPopover | null>} openLinkPopoverRef - Stable ref to the function that opens the link popover, wired to the `Ctrl/Cmd+K` keyboard shortcut extension.
 * @property {`image/${string}`[]} [allowedMimeTypes] - MIME types accepted for image upload via paste and drop.
 */
export type UseRichTextEditorParams = Pick<
  RichTextEditorProps,
  | "value"
  | "defaultValue"
  | "onChange"
  | "onJsonChange"
  | "contentType"
  | "onMarkdownChange"
  | "extensions"
  | "placeholder"
  | "disabled"
  | "allowTextFormat"
  | "allowTextAlignment"
  | "allowLists"
  | "allowChecklist"
  | "allowCode"
  | "allowBlockquote"
  | "allowLink"
  | "allowMedia"
  | "allowMention"
  | "onImageUpload"
  | "isAllowedImageSrc"
> & {
  allowedMimeTypes?: `image/${string}`[];
  onImageUploadRef: MutableRefObject<RichTextEditorProps["onImageUpload"]>;
  mentionCallbacksRef: MutableRefObject<MentionCallbacks>;
  onYoutubePasteRef: MutableRefObject<OnYoutubePaste>;
  openLinkPopoverRef: MutableRefObject<OpenLinkPopover | null>;
};

/**
 * Internal hook that initializes and manages the TipTap editor instance for RichTextEditor.
 *
 * Conditionally registers TipTap extensions based on the `allow*` feature flags so that
 * only the capabilities needed for a given editor configuration are included. Handles both
 * controlled mode (via `value` + `onChange`) and uncontrolled mode (via `defaultValue`),
 * and syncs the `disabled` prop to the editor's editable state.
 *
 * @param params - Configuration derived from RichTextEditorProps plus stable callback refs.
 * @returns The TipTap `Editor` instance, or `null` during SSR / before mount.
 */
export const useRichTextEditor = ({
  value,
  defaultValue,
  onChange,
  onJsonChange,
  contentType,
  onMarkdownChange,
  extensions,
  placeholder,
  disabled = false,
  allowTextFormat = true,
  allowTextAlignment = true,
  allowLists = false,
  allowChecklist = false,
  allowCode = false,
  allowBlockquote = false,
  allowLink = false,
  allowMedia = false,
  allowMention = false,
  allowedMimeTypes,
  isAllowedImageSrc,
  onImageUploadRef,
  mentionCallbacksRef,
  onYoutubePasteRef,
  openLinkPopoverRef,
}: UseRichTextEditorParams) => {
  const isControlled = value !== undefined;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        codeBlock: false,
        horizontalRule: false,
        bold: allowTextFormat ? {} : false,
        italic: allowTextFormat ? {} : false,
        strike: allowTextFormat ? {} : false,
        heading: allowTextFormat ? {} : false,
        code: allowCode ? {} : false,
        blockquote: allowBlockquote ? {} : false,
        bulletList: allowLists ? {} : false,
        orderedList: allowLists ? {} : false,
        listItem: allowLists ? {} : false,
        underline: allowTextFormat ? {} : false,
        link: allowLink
          ? { openOnClick: false, enableClickSelection: true }
          : false,
      }),
      ...(allowTextFormat ? [TextStyle, FontSize] : []),
      ...(allowTextAlignment
        ? [
            TextAlign.configure({
              types: [
                "heading",
                "paragraph",
                ...(allowMedia ? ["image", "youtube"] : []),
              ],
            }),
          ]
        : []),
      ...(allowMedia
        ? [
            createSafeImageExtension(isAllowedImageSrc).configure({
              inline: false,
              HTMLAttributes: { draggable: false },
            }),
            createYoutubeExtension(onYoutubePasteRef),
          ]
        : []),
      ...(allowMedia
        ? [createImageUploadExtension(onImageUploadRef, allowedMimeTypes)]
        : []),
      ...(allowChecklist
        ? [TaskList, TaskItem.configure({ nested: true })]
        : []),
      ...(allowMention ? [createMentionExtension(mentionCallbacksRef)] : []),
      ...(allowLink
        ? [createLinkKeyboardShortcutExtension(openLinkPopoverRef)]
        : []),
      Placeholder.configure({ placeholder }),
      ...(contentType === "markdown" || onMarkdownChange ? [Markdown] : []),
      ...(extensions ?? []),
    ],
    injectCSS: false,
    content: isControlled ? value : defaultValue,
    contentType,
    editable: !disabled,
    onUpdate: ({ editor: e }) => {
      onChange?.(e.getHTML());
      onJsonChange?.(e.getJSON());
      onMarkdownChange?.(e.getMarkdown());
    },
  });

  useEffect(() => {
    if (!editor || !isControlled) return;
    const currentValue =
      contentType === "markdown" ? editor.getMarkdown() : editor.getHTML();
    if (currentValue !== value) {
      editor.commands.setContent(value ?? "", {
        emitUpdate: false,
        contentType,
      });
    }
  }, [editor, isControlled, value, contentType]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  return editor;
};
